import React, { useMemo } from "react";
import ReactDOM from "react-dom";
import { ArrowRight, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Spinner } from "~/components/ui/spinner";
import SearchQuery from "~/searchQuery";
import savedSearchStore, { SavedSearchInfo } from "~/stores/savedSearch";
import { ReqStatus } from "~/util";
import { observer } from "mobx-react";

const GlobalElem = (props: { children: React.ReactNode }) => ReactDOM.createPortal(props.children, document.body);

interface SuggestionListProps {
  suggestions: SavedSearchInfo[];
  onSearch: (sq: SearchQuery, saved: SavedSearchInfo) => void;
  shown: boolean;
  onClose: () => void;
}

const LoadingWrapper = (props: { children: React.ReactNode; loading: boolean }) => {
  if (props.loading) {
    return (
      <div className="flex h-full w-full flex-[1_1_100%] items-center justify-center py-4">
        <Spinner className="size-5" />
      </div>
    );
  }
  return <>{props.children}</>;
};

const SuggestionsList = observer((props: SuggestionListProps) => {
  const { onClose, shown, suggestions, onSearch } = props;
  const loading = savedSearchStore.f.deletionStatus === ReqStatus.InProcess;
  const rendered = useMemo(() => {
    const items = suggestions.map((sug, idx) => {
      const title = `Search for "${sug.search_value}"`;
      return (
        <div
          key={idx}
          role="button"
          tabIndex={0}
          title={title}
          aria-label={title}
          className="flex w-full cursor-pointer flex-row items-center justify-between px-4 py-2 text-sm [color:var(--text-01)] hover:bg-[var(--ui-02)]"
          onClick={(e) => {
            e.preventDefault();
            onSearch(SearchQuery.fromSavedSearch(sug), sug);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSearch(SearchQuery.fromSavedSearch(sug), sug);
            }
          }}
        >
          <div className="flex min-w-0 flex-row items-center">
            <ArrowRight className="mr-4 size-3.5 shrink-0 [color:var(--ui-05)]" />
            <span className="truncate">{sug.description}</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Remove"
            className="size-7 shrink-0"
            onClick={async (e) => {
              e.stopPropagation();
              await savedSearchStore.f.delete(sug.id);
            }}
          >
            <X className="size-3.5" />
          </Button>
        </div>
      );
    });
    return <LoadingWrapper loading={loading}>{items}</LoadingWrapper>;
  }, [suggestions, loading]);
  return (
    <>
      {shown && (
        <GlobalElem>
          <div
            className="absolute left-0 top-0 h-full w-full"
            style={{ zIndex: 9999999 }}
            onClick={() => onClose()}
          />
        </GlobalElem>
      )}
      <div
        className="absolute left-0 top-0 max-h-[280px] w-full overflow-auto rounded-b-[4px] bg-white shadow-[0px_0px_2px_0px_#00000017,0px_0px_8px_0px_#00000012]"
        style={{ zIndex: 10000000 }}
      >
        {rendered}
      </div>
    </>
  );
});

export default SuggestionsList;
